import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

export type SaveStatus = 'idle' | 'saving' | 'saved' | 'error'

/** How long the green tick stays up before the affordance returns to its resting icon. */
const SAVED_DURATION = 1500

function saveErrorMessage(error): string | undefined {
  return error?.response?.data?.message || error?.message
}

export interface IUseSaveStatus {
  status: SaveStatus
  errorMessage?: string
  /** Resolves true when the save succeeded. On failure the status stays `error` until cleared. */
  runSave: (save: () => Promise<void>) => Promise<boolean>
  clearError: () => void
}

/**
 * The idle → saving → saved → idle lifecycle behind an inline edit, with the error state sticky
 * until the next attempt.
 *
 * Shared by the users table's cells and the location page's settings rows so the two surfaces
 * cannot drift on what "saved" looks like or how long it lasts.
 */
export function useSaveStatus(): IUseSaveStatus {
  const [status, setStatus] = useState<SaveStatus>('idle')
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined)
  const savedTimer = useRef<ReturnType<typeof setTimeout>>()

  const stopTimer = () => {
    if (savedTimer.current) {
      clearTimeout(savedTimer.current)
      savedTimer.current = undefined
    }
  }

  useEffect(() => stopTimer, [])

  const runSave = async (save: () => Promise<void>) => {
    // A save landing inside the previous one's window would otherwise have its spinner cleared
    // early by the older timer.
    stopTimer()
    setStatus('saving')
    setErrorMessage(undefined)
    try {
      await save()
      setStatus('saved')
      savedTimer.current = setTimeout(() => setStatus('idle'), SAVED_DURATION)
      return true
    } catch (error) {
      setErrorMessage(saveErrorMessage(error))
      setStatus('error')
      return false
    }
  }

  const clearError = () => {
    setStatus((current) => (current === 'error' ? 'idle' : current))
    setErrorMessage(undefined)
  }

  return { status, errorMessage, runSave, clearError }
}

/** A failed keyed save, naming where the saved thing is now known when that is no longer its key. */
export interface SaveError extends Error {
  movedTo?: string
}

interface KeyedStatus {
  status: SaveStatus
  errorMessage?: string
}

export interface IKeyedSaveStatus {
  statusOf: (key: string) => SaveStatus
  errorOf: (key: string) => string | undefined
  /**
   * Runs one save under `key`, independent of every other key. When what the key named has been
   * re-identified, the save resolves with, or fails with a SaveError naming, the key the mark also goes to.
   */
  runSave: (key: string, save: () => Promise<string | void>) => Promise<boolean>
  clearError: (key: string) => void
}

/** useSaveStatus once per key, so saves on different cells neither share nor clear each other's state. */
export function useKeyedSaveStatus(): IKeyedSaveStatus {
  const [statuses, setStatuses] = useState<Record<string, KeyedStatus>>({})
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>())

  useEffect(() => {
    const pending = timers.current
    return () => pending.forEach((timer) => clearTimeout(timer))
  }, [])

  const set = useCallback((key: string, next: KeyedStatus | null) => {
    clearTimeout(timers.current.get(key))
    timers.current.delete(key)
    setStatuses((current) => {
      const copy = { ...current }
      if (next) copy[key] = next
      else delete copy[key]
      return copy
    })
  }, [])

  const markSaved = useCallback(
    (key: string) => {
      set(key, { status: 'saved' })
      timers.current.set(
        key,
        setTimeout(() => set(key, null), SAVED_DURATION)
      )
    },
    [set]
  )

  const runSave = useCallback(
    async (key: string, save: () => Promise<string | void>) => {
      set(key, { status: 'saving' })
      try {
        const movedTo = await save()
        markSaved(key)
        if (movedTo && movedTo !== key) markSaved(movedTo)
        return true
      } catch (error) {
        const failed: KeyedStatus = { status: 'error', errorMessage: saveErrorMessage(error) || undefined }
        set(key, failed)
        const movedTo = (error as SaveError)?.movedTo
        if (movedTo && movedTo !== key) set(movedTo, failed)
        return false
      }
    },
    [set, markSaved]
  )

  // An errored key has no saved timer pending, so only the status needs dropping.
  const clearError = useCallback((key: string) => {
    setStatuses((current) => {
      if (current[key]?.status !== 'error') return current
      const copy = { ...current }
      delete copy[key]
      return copy
    })
  }, [])

  return useMemo(
    () => ({
      statusOf: (key: string) => statuses[key]?.status ?? 'idle',
      errorOf: (key: string) => statuses[key]?.errorMessage,
      runSave,
      clearError,
    }),
    [statuses, runSave, clearError]
  )
}
